import { Component, inject, input, model, output, computed, signal, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { resolveLocalizedValue } from '../../../core/services/form-config-adapter.service';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import * as XLSX from 'xlsx';
import { ImportConfig, ModuleFormConfig } from '../../models/page-config.model';
import { ImportReviewGridComponent } from '../import-review-grid/import-review-grid.component';
import { TableColumn } from '../../components/dynamic-table/dynamic-table.interface';
import { OrgTimezoneService } from '../../../core/services/org-timezone.service';
import { UiOrchestratorService } from '../../../core/services/ui-orchestrator.service';

export interface ImportValidationResult {
  row: number;
  field: string;
  message: string;
  type: 'error' | 'warning';
}

export interface ParsedImportData {
  headers?: string[];
  rows?: Record<string, any>[];
  validRows: Record<string, any>[];
  displayRows?: Record<string, any>[];
  validationResults: ImportValidationResult[];
  valid: boolean;
  totalRows: number;
  errorCount: number;
}

@Component({
  selector: 'app-csv-import-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    TooltipModule,
    TagModule,
    ProgressBarModule,
    SelectModule,
    ImportReviewGridComponent
  ],
  templateUrl: './csv-import-dialog.component.html'
})
export class CsvImportDialogComponent implements OnDestroy {

  visible       = model<boolean>(false);
  importConfig  = input.required<ImportConfig>();
  moduleConfig  = input<ModuleFormConfig | any>();

  importReady   = output<ParsedImportData>();

  private ui             = inject(UiOrchestratorService);
  private http           = inject(HttpClient);
  readonly orgTz         = inject(OrgTimezoneService);
  private get baseUrl(): string {
    return this.importConfig().baseUrl
      ?? (environment as any).baseUrl
      ?? environment.apiUrl;
  }

  // --- Upload State ---
  isDragOver    = signal(false);
  selectedFile  = signal<File | null>(null);
  parseResult   = signal<ParsedImportData | null>(null);
  isProcessing  = signal(false);
  batchTimezone = signal<string>('');

  constructor() {
    this.orgTz.load();
  }

  // --- Computed ---
  
  dynamicFields = computed(() => {
    const mConfig = this.moduleConfig();
    if (mConfig && mConfig.steps) {
      const fields: { field: string, label: string, required: boolean, sampleValue: string, hint?: string }[] = [];

      // Detect mode: if any field uses csv_include, switch to opt-in mode
      const allSchemaFields = mConfig.steps.flatMap((s: any) => s.sections?.flatMap((sec: any) => sec.fields ?? []) ?? []);
      const useIncludeMode = allSchemaFields.some((f: any) => f.csv_include === true);

      mConfig.steps.forEach((step: any) => {
        step.sections?.forEach((sec: any) => {
          sec.fields?.forEach((f: any) => {
             // Always skip complex/unsupported field types
             if (f.type === 'table-editor' || f.type === 'accordion-array' || f.type === 'dynamic_table' || f.type === 'photo_upload') {
               return;
             }

             // opt-in mode: only include fields explicitly marked csv_include
             // opt-out mode (legacy): skip fields marked csv_exclude
             if (useIncludeMode ? f.csv_include !== true : f.csv_exclude === true) {
               return;
             }

             let sampleValue = f.csv_sample;
             if (!sampleValue) {
               if (f.placeholder && f.placeholder.toLowerCase().startsWith('e.g.')) {
                 sampleValue = f.placeholder.substring(4).trim();
               } else if (f.options_source === 'static' && Array.isArray(f.options) && f.options.length > 0) {
                 sampleValue = String(f.options[0].label ?? f.options[0].value ?? '');
               } else if (f.type === 'date' || f.type === 'datetime') {
                 sampleValue = '2024-01-15';
               } else if (f.type === 'daterange') {
                 sampleValue = '2024-01-15,2024-12-31';
               } else if (f.options_source === 'api') {
                 sampleValue = '';
               } else {
                 sampleValue = '';
               }
             }

             fields.push({
               field: f.id || f.key || '',
               label: resolveLocalizedValue(f.label) || f.key || '',
               required: !!f.validation?.required || !!f.validators?.length,
               sampleValue: sampleValue
             });
          });
        });
      });
      return fields;
    }
    return this.importConfig().fields;
  });

  csvHeaders = computed(() =>
    this.dynamicFields().map(f => f.field).join(',')
  );

  csvSampleRows = computed(() => {
    const fields = this.dynamicFields();
    // Single sample row using only known-valid sample values
    const row = fields.map(f => {
      const val = f.sampleValue ?? '';
      // Wrap values containing commas in quotes
      return val.includes(',') ? `"${val}"` : val;
    }).join(',');
    return [row];
  });

  csvTemplateContent = computed(() => {
    const rows = [this.csvHeaders(), ...this.csvSampleRows()];
    return rows.join('\n');
  });

  dropzoneClass = computed(() => {
    const base = 'upload-dropzone';
    if (this.isDragOver())   return base + ' drag-over';
    if (this.parseResult()?.valid === false) return base + ' has-error';
    if (this.parseResult()?.valid === true)  return base + ' has-file';
    if (this.selectedFile()) return base + ' has-file';
    return base;
  });

  groupedErrors = computed(() => {
    const res = this.parseResult();
    if (!res || res.valid) return [];

    const fieldLabels = new Map<string, string>();
    for (const f of this.dynamicFields()) {
      fieldLabels.set(f.field, f.label);
    }

    const groups = new Map<number, { label: string; message: string }[]>();
    for (const err of res.validationResults) {
      if (!groups.has(err.row)) groups.set(err.row, []);
      const label = fieldLabels.get(err.field) ?? err.field;
      const msg   = /required/i.test(err.message)         ? 'required'
                  : /invalid option/i.test(err.message)   ? 'invalid value'
                  : /invalid date/i.test(err.message)     ? 'use YYYY-MM-DD format'
                  : /invalid format/i.test(err.message)   ? 'invalid format'
                  : err.message;
      groups.get(err.row)!.push({ label, message: msg });
    }

    return Array.from(groups.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([row, lines]) => ({ row, lines }));
  });

  canSubmit = computed(() => {
    const result = this.parseResult();
    if (!result || this.isProcessing()) return false;
    if (this.importConfig().validateEndpoint) return result.validRows.length > 0;
    return result.valid;
  });

  // Dynamically create TableColumns for the Review Grid
  reviewColumns = computed<TableColumn[]>(() => {
    return this.dynamicFields().map(f => ({
      field: f.field,
      header: f.label,
      type: 'text',
      sortable: false
    }));
  });

  // --- Private ---
  private objectUrl: string | null = null;

  // --- Key remapping ---
  // Matches parsed CSV headers (which may have different casing/spacing) to expected field IDs.
  private remapRowKeys(row: Record<string, any>, expectedFields: string[]): Record<string, any> {
    const result: Record<string, any> = {};
    // Build a lookup: normalised key → expected field ID
    const norm = (s: string) => s.toLowerCase().replace(/[\s_-]+/g, '');
    const lookup = new Map(expectedFields.map(f => [norm(f), f]));
    for (const [key, val] of Object.entries(row)) {
      const mapped = lookup.get(norm(key));
      result[mapped ?? key.trim()] = typeof val === 'string' ? val.trim() : val;
    }
    return result;
  }

  // --- Date normalization ---

  private getDateFieldIds(): Set<string> {
    const ids = new Set<string>();
    const config = this.moduleConfig();
    if (!config?.steps) return ids;
    for (const step of config.steps) {
      for (const section of step.sections ?? []) {
        for (const field of section.fields ?? []) {
          if (field.type === 'date' || field.type === 'datetime') {
            ids.add(field.id ?? field.key ?? '');
          }
        }
      }
    }
    return ids;
  }

  private normalizeDateFields(row: Record<string, any>, dateFieldIds: Set<string>): Record<string, any> {
    const result = { ...row };
    for (const key of Object.keys(result)) {
      if (!dateFieldIds.has(key)) continue;
      const val = result[key];
      if (!val || typeof val !== 'string') continue;

      // Already YYYY-MM-DD — no conversion needed
      if (/^\d{4}-\d{2}-\d{2}$/.test(val)) continue;

      // MM/DD/YYYY or M/D/YYYY (common Excel / US export format)
      const mdy = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (mdy) {
        result[key] = `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
        continue;
      }

      // DD/MM/YYYY or D/M/YYYY (European format)
      const dmy = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (dmy && Number(dmy[1]) > 12) {
        result[key] = `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
        continue;
      }

      // ISO datetime string — extract date portion directly, no timezone shift
      const isoMatch = val.match(/^(\d{4}-\d{2}-\d{2})/);
      if (isoMatch) {
        result[key] = isoMatch[1];
        continue;
      }

      // Fallback: parse and use LOCAL time (not UTC) to avoid day-shift in UTC+ zones
      const parsed = new Date(val);
      if (!isNaN(parsed.getTime())) {
        const y = parsed.getFullYear();
        const m = String(parsed.getMonth() + 1).padStart(2, '0');
        const d = String(parsed.getDate()).padStart(2, '0');
        result[key] = `${y}-${m}-${d}`;
      }
    }
    return result;
  }

  private resolveLabelsForDisplay(row: Record<string, any>): Record<string, any> {
    const config = this.moduleConfig();
    if (!config?.steps) return row;
    const result = { ...row };
    for (const step of config.steps ?? []) {
      for (const section of step.sections ?? []) {
        for (const field of section.fields ?? []) {
          if (field.options_source !== 'static' || !Array.isArray(field.options)) continue;
          const fieldId = field.id ?? field.key;
          const cellValue = result[fieldId];
          if (cellValue === undefined || cellValue === null || cellValue === '') continue;
          const match = field.options.find((o: any) => String(o.value) === String(cellValue));
          if (match) result[fieldId] = String(match.label);
        }
      }
    }
    return result;
  }

  private normalizeOptionLabels(row: Record<string, any>): Record<string, any> {
    const config = this.moduleConfig();
    if (!config?.steps) return row;
    const result = { ...row };
    for (const step of config.steps ?? []) {
      for (const section of step.sections ?? []) {
        for (const field of section.fields ?? []) {
          if (field.options_source !== 'static' || !Array.isArray(field.options)) continue;
          const fieldId = field.id ?? field.key;
          const cellValue = result[fieldId];
          if (cellValue === undefined || cellValue === null || cellValue === '') continue;
          const cellStr = String(cellValue).trim().toLowerCase();
          // Already a valid value — leave it
          const matchByValue = field.options.find((o: any) => String(o.value).toLowerCase() === cellStr);
          if (matchByValue) continue;
          // Exact label match (case-insensitive) — convert to value
          const matchByLabel = field.options.find((o: any) => String(o.label).toLowerCase() === cellStr);
          if (matchByLabel) { result[fieldId] = String(matchByLabel.value); continue; }
          // Partial label match — auto-correct only when ≥3 chars and exactly one option matches
          if (cellStr.length >= 3) {
            const partialMatches = field.options.filter((o: any) =>
              String(o.label).toLowerCase().includes(cellStr)
            );
            if (partialMatches.length === 1) result[fieldId] = String(partialMatches[0].value);
          }
        }
      }
    }
    return result;
  }

  // --- Download Template ---
  downloadTemplate() {
    const blob = new Blob(
      [this.csvTemplateContent()],
      { type: 'text/csv;charset=utf-8;' }
    );
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href     = url;
    link.download = `${this.importConfig().templateFilename}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
    
    this.ui.toast({
      severity: 'info',
      summary:  'Template Downloaded',
      detail:   'Fill in your data and upload the file.',
      life:     3000
    });
  }

  // --- Drag and Drop ---
  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  onDragLeave() {
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) this.handleFile(file);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file  = input.files?.[0];
    if (file) this.handleFile(file);
    input.value = '';
  }

  // --- File Validation & Parsing ---
  handleFile(file: File) {
    const isValidType = file.name.endsWith('.csv') || file.name.endsWith('.xlsx');
    if (!isValidType) {
      this.ui.toast({
        severity: 'error',
        summary:  'Invalid File Type',
        detail:   'Please upload a .csv or .xlsx file only.',
        life:     4000
      });
      return;
    }

    const maxMb   = this.importConfig().maxFileSizeMb || 5;
    const sizeMb  = file.size / (1024 * 1024);
    if (sizeMb > maxMb) {
      this.ui.toast({
        severity: 'error',
        summary:  'File Too Large',
        detail:   `Maximum file size is ${maxMb}MB. Your file is ${sizeMb.toFixed(1)}MB.`,
        life:     4000
      });
      return;
    }

    this.selectedFile.set(file);
    this.parseResult.set(null);
    this.isProcessing.set(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const wb   = XLSX.read(data, { type: 'array' });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rawJson = XLSX.utils.sheet_to_json<Record<string, any>>(ws, { raw: false, defval: '', dateNF: 'YYYY-MM-DD' });
        const expectedFields = this.dynamicFields().map(f => f.field);
        const dateFieldIds   = this.getDateFieldIds();
        const json = rawJson
          .map(row => this.remapRowKeys(row, expectedFields))
          .map(row => this.normalizeDateFields(row, dateFieldIds))
          .map(row => this.normalizeOptionLabels(row));

        const config = this.importConfig();

        // Skip backend validation when no validateEndpoint is configured
        if (!config.validateEndpoint) {
          const errors: ImportValidationResult[] = [];
          const requiredFields = this.dynamicFields().filter(f => f.required).map(f => f.field);

          json.forEach((row, i) => {
            // Required field check (always)
            for (const field of requiredFields) {
              if (!row[field]?.toString().trim()) {
                errors.push({ row: i, field, message: 'required', type: 'error' });
              }
            }

            // Extra format checks for bulk-upload (batchTimezone) mode
            if (config.batchTimezone) {
              const dateVal = row['date']?.toString().trim();
              const timeVal = row['time']?.toString().trim();
              const tzVal   = row['timezone']?.toString().trim();

              if (dateVal) {
                const validDate = /^\d{4}-\d{2}-\d{2}$/.test(dateVal)
                  || /^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(dateVal);
                if (!validDate) {
                  errors.push({ row: i, field: 'date', message: 'Use M/D/YYYY or YYYY-MM-DD format', type: 'error' });
                }
              }

              if (timeVal) {
                if (!/^\d{1,2}:\d{2}$/.test(timeVal)) {
                  errors.push({ row: i, field: 'time', message: 'Use H:mm or HH:mm format', type: 'error' });
                }
              }

              if (tzVal) {
                try {
                  Intl.DateTimeFormat(undefined, { timeZone: tzVal });
                } catch {
                  errors.push({ row: i, field: 'timezone', message: `"${tzVal}" is not a valid IANA timezone`, type: 'error' });
                }
              }
            }
          });
          const validRows = errors.length === 0 ? json : json.filter((_, i) => !errors.some(e => e.row === i));
          const result: ParsedImportData = {
            rows: json,
            validRows,
            displayRows: validRows.map(row => this.resolveLabelsForDisplay(row)),
            validationResults: errors,
            valid: errors.length === 0,
            totalRows: json.length,
            errorCount: errors.length
          };
          this.parseResult.set(result);
          this.isProcessing.set(false);
          if (result.valid) {
            this.ui.toast({
              severity: 'success',
              summary:  'File Ready',
              detail:   `${json.length} records parsed. Review and confirm to upload.`,
              life:     3000
            });
          } else {
            this.ui.toast({
              severity: 'warn',
              summary:  'Missing Required Fields',
              detail:   `${errors.length} issue(s) found. Fix the file and re-upload.`,
              life:     5000
            });
          }
          return;
        }

        // Send to backend for validation
        const formKey = config.formKey ?? '';
        let validateUrl = config.validateEndpoint
          ? `${this.baseUrl}${config.validateEndpoint}`
          : `${this.baseUrl}/employees/bulk/validate`;
        if (formKey) {
          validateUrl += `${validateUrl.includes('?') ? '&' : '?'}form_key=${encodeURIComponent(formKey)}`;
        }
        this.http.post<ParsedImportData>(validateUrl, json).subscribe({
          next: (res) => {
            const displayRows = res.validRows.map(row => this.resolveLabelsForDisplay(row));
            this.parseResult.set({ ...res, rows: json, displayRows });
            this.isProcessing.set(false);

            if (res.valid) {
              this.ui.toast({
                severity: 'success',
                summary:  'Validation Passed',
                detail:   `Please review the ${res.totalRows} records before confirming.`,
                life:     3000
              });
            } else {
              this.ui.toast({
                severity: 'warn',
                summary:  'Validation Issues Found',
                detail:   `${res.errorCount} issue(s) found. Review and fix before importing.`,
                life:     5000
              });
            }
          },
          error: () => {
            this.isProcessing.set(false);
            this.ui.toast({
              severity: 'error',
              summary: 'Validation Failed',
              detail: 'Failed to reach the validation server.'
            });
          }
        });
      } catch {
        this.isProcessing.set(false);
        this.ui.toast({
          severity: 'error',
          summary:  'Parse Error',
          detail:   'Could not read the file. Ensure it is a valid CSV or Excel format.',
          life:     5000
        });
      }
    };
    reader.readAsArrayBuffer(file);
  }

  clearFile() {
    this.selectedFile.set(null);
    this.parseResult.set(null);
  }

  /**
   * Converts a wall-clock date+time in a given IANA timezone to a UTC ISO string.
   * Handles date formats: YYYY-MM-DD, M/D/YYYY, M/D/YY
   * Handles time formats: H:mm, HH:mm
   */
  private zonedToUtcIso(dateStr: string, timeStr: string, tz: string): string {
    // Normalise date → YYYY-MM-DD
    let d = (dateStr ?? '').toString().trim();
    const mdyShort = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
    if (mdyShort) d = `20${mdyShort[3]}-${mdyShort[1].padStart(2, '0')}-${mdyShort[2].padStart(2, '0')}`;
    const mdyLong  = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdyLong)  d = `${mdyLong[3]}-${mdyLong[1].padStart(2, '0')}-${mdyLong[2].padStart(2, '0')}`;

    // Normalise time → HH:mm
    const tp = ((timeStr ?? '').toString().trim()).match(/^(\d{1,2}):(\d{2})$/);
    const t  = tp ? `${tp[1].padStart(2, '0')}:${tp[2]}` : '00:00';

    // Interpret the wall-clock string as if it were UTC, then compute offset
    const utcGuess = new Date(`${d}T${t}:00Z`);
    const parts    = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }).formatToParts(utcGuess);
    const g = (type: string) => parseInt(parts.find(p => p.type === type)!.value);
    const tzMs = new Date(
      `${g('year')}-${String(g('month')).padStart(2,'0')}-${String(g('day')).padStart(2,'0')}` +
      `T${String(g('hour')).padStart(2,'0')}:${String(g('minute')).padStart(2,'0')}:00Z`
    ).getTime();

    // Offset = diff between what UTC guess shows in tz vs the original UTC guess
    return new Date(utcGuess.getTime() + (utcGuess.getTime() - tzMs)).toISOString();
  }

  onSubmit() {
    const result = this.parseResult();
    if (!result) return;
    const hasBackendValidation = !!this.importConfig().validateEndpoint;
    // Backend-validated imports: allow partial submission (backend inserts valid rows, logs failures)
    // Locally-validated imports: require full pass
    if (hasBackendValidation ? result.validRows.length === 0 : !result.valid) return;

    const config  = this.importConfig();
    const batchTz = this.batchTimezone();

    let rows = result.validRows;

    // When batchTimezone is enabled, convert date+time → single UTC ISO `time` field
    if (config.batchTimezone) {
      rows = rows.map(row => {
        const tz  = (row['timezone'] as string)?.trim() || batchTz || 'UTC';
        const utc = this.zonedToUtcIso(row['date'], row['time'], tz);
        const { date: _d, timezone: _tz, time: _t, ...rest } = row as any;
        return { ...rest, time: utc };
      });
    }

    // Wrap in payloadKey if specified (e.g. { "records": [...] })
    const payload: any = config.payloadKey ? { [config.payloadKey]: rows } : rows;

    this.isProcessing.set(true);
    let uploadUrl = config.uploadEndpoint
      ? `${this.baseUrl}${config.uploadEndpoint}`
      : `${this.baseUrl}/employees/bulk`;
    const uploadFormKey = config.formKey ?? '';
    if (uploadFormKey) {
      uploadUrl += `${uploadUrl.includes('?') ? '&' : '?'}form_key=${encodeURIComponent(uploadFormKey)}`;
    }

    this.http.post<any>(uploadUrl, payload).subscribe({
      next: (res) => {
        this.isProcessing.set(false);
        this.ui.toast({
          severity: 'success',
          summary: 'Import Successful',
          detail: res.message ?? `${rows.length} records uploaded successfully.`
        });
        this.importReady.emit(result);
        this.close();
      },
      error: () => {
        this.isProcessing.set(false);
        this.ui.toast({
          severity: 'error',
          summary: 'Import Failed',
          detail: 'Could not complete the bulk import.'
        });
      }
    });
  }

  close() {
    this.visible.set(false);
    this.selectedFile.set(null);
    this.parseResult.set(null);
    this.isProcessing.set(false);
    this.isDragOver.set(false);
    this.batchTimezone.set('');
  }

  ngOnDestroy() {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
  }
}
